import { EUserRoles } from '@models/user';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface TProgressRow {
  companyCode: string | null;
  companyName: string | null;
  storeCode: string | null;
  storeName: string | null;
  storeDisplayText: string | null;
  departmentCode?: string | null;
  departmentName?: string | null;
  inventoryStartDateDisplay: string;
  totalCount: number;
  confirmedCount: number;
  confirmedRate: number;
  confirmedRateDisplay: string;
  unconfirmedCount: number;
  unusedCount: number;
  unlistedCount: number;
  moveCount: number;
  removeCount: number;
  ledgerLink: string | null;
  /** [4.11] ステータス — populated on store (parent) rows only; null on department rows/total row. */
  statusCode?: string | null;
  statusDisplay?: string | null;
  /** t_inventory_store_sessions.id — used by [4.12] 承認/差し戻し buttons. */
  inventoryStoreSessionId?: string | null;
  departments?: TProgressRow[];
  /** Client-side only — unique key for antd Table row/expand tracking. */
  key?: string;
}

export interface TProgressTotalRow {
  totalCount: number;
  confirmedCount: number;
  confirmedRate: number;
  confirmedRateDisplay: string;
  unconfirmedCount: number;
  unusedCount: number;
  unlistedCount: number;
  moveCount: number;
  removeCount: number;
}

export interface TProgressData {
  inventoryYearMonth: string;
  inventoryYearMonthDisplay: string;
  sessionCode: string;
  totalRow: TProgressTotalRow;
  rows: TProgressRow[];
}

export interface TYearMonthOption {
  value: string; // YYYYMM
  label: string; // YYYY/MM
}

export interface TCompanyOption {
  value: string;
  label: string;
}

export interface TStoreOption {
  value: string;
  label: string;
  companyCode?: string;
}

export const ALL_VALUE = 'ALL';

export function isAdminLikeRole(role: EUserRoles[] | undefined): boolean {
  if (!role || role.length === 0) {
    return false;
  }
  return role.includes(EUserRoles.ADMIN);
}

// ─── Constants ────────────────────────────────────────────────────────────────────

export const PROGRESS_REVIEW_CONSTANT = {
  TITLE: {
    PAGE: '固定資産棚卸 進捗確認',
  },
  LABEL: {
    YEAR_MONTH: '年月',
    COMPANY: '会社',
    STORE: '店舗',
    COMPANY_COL: '会社',
    STORE_COL: '店舗',
    DEPARTMENT_COL: '部門',
    START_DATE_COL: '棚卸開始日',
    TOTAL_COL: '全件数',
    CONFIRMED_COL: '確認済',
    RATE_COL: '%',
    UNCONFIRMED_COL: '未確認',
    UNUSED_COL: '未使用',
    UNLISTED_COL: '不記載',
    MOVE_COL: '移動',
    REMOVE_COL: '除却',
    STATUS_COL: 'ステータス',
    TOTAL_ROW_LABEL: '合計',
    ALL_COMPANIES: '全て',
    ALL_STORES: '全て',
    NO_DATA: '該当するデータがありません。',
  },
  MESSAGE: {
    E01: 'データの取得に失敗しました。再度お試しください。',
  },
} as const;

// ─── Mock data ─────────────────────────────────────────────────────────────────

// [3.1] 年月 / [3.2] 会社 / [3.3] 店舗 dropdowns are populated from the real DB via
// inventorySessionApi / layoutApi
// (api/v1/company, api/v1/store — same endpoints already used by VAMS_PC_004
// FixedAssetLedger). See useProgressReviewPage.tsx fetchCompanies/fetchStores.
export const ALL_COMPANIES_OPTION: TCompanyOption = {
  value: ALL_VALUE,
  label: '全て',
};

export const ALL_STORES_OPTION: TStoreOption = {
  value: '',
  label: '全て',
};

// BE-PENDING: Replace with GET /api/v1/inventory/progress response (api-integration.md §2.1)
export const MOCK_PROGRESS_DATA: TProgressData = {
  inventoryYearMonth: '202607',
  inventoryYearMonthDisplay: '2026/07',
  sessionCode: 'SESSION-2026-07',
  totalRow: {
    totalCount: 1692,
    confirmedCount: 1468,
    confirmedRate: 86.76,
    confirmedRateDisplay: '87%',
    unconfirmedCount: 158,
    unusedCount: 42,
    unlistedCount: 24,
    moveCount: 0,
    removeCount: 0,
  },
  rows: [
    {
      companyCode: 'SM',
      companyName: 'SM社名',
      storeCode: '001',
      storeName: '恵那',
      storeDisplayText: '001 恵那',
      inventoryStartDateDisplay: '2026/07/01',
      totalCount: 344,
      confirmedCount: 335,
      confirmedRate: 97.38,
      confirmedRateDisplay: '97%',
      unconfirmedCount: 0,
      unusedCount: 5,
      unlistedCount: 4,
      moveCount: 0,
      removeCount: 0,
      ledgerLink: '/fixedassetledger?inventoryYearMonth=202607&storeCode=001',
    },
    {
      companyCode: 'SM',
      companyName: 'SM社名',
      storeCode: '002',
      storeName: '明智',
      storeDisplayText: '002 明智',
      inventoryStartDateDisplay: '-',
      totalCount: 128,
      confirmedCount: 0,
      confirmedRate: 0.0,
      confirmedRateDisplay: '0%',
      unconfirmedCount: 128,
      unusedCount: 0,
      unlistedCount: 0,
      moveCount: 0,
      removeCount: 0,
      ledgerLink: '/fixedassetledger?inventoryYearMonth=202607&storeCode=002',
    },
    {
      companyCode: 'SM',
      companyName: 'SM社名',
      storeCode: '003',
      storeName: '青葉台',
      storeDisplayText: '003 青葉台',
      inventoryStartDateDisplay: '2026/07/03',
      totalCount: 512,
      confirmedCount: 480,
      confirmedRate: 93.75,
      confirmedRateDisplay: '94%',
      unconfirmedCount: 10,
      unusedCount: 12,
      unlistedCount: 10,
      moveCount: 0,
      removeCount: 0,
      ledgerLink: '/fixedassetledger?inventoryYearMonth=202607&storeCode=003',
    },
    {
      companyCode: 'SM',
      companyName: 'SM社名',
      storeCode: '004',
      storeName: '緑ヶ丘',
      storeDisplayText: '004 緑ヶ丘',
      inventoryStartDateDisplay: '2026/07/02',
      totalCount: 456,
      confirmedCount: 423,
      confirmedRate: 92.76,
      confirmedRateDisplay: '93%',
      unconfirmedCount: 5,
      unusedCount: 20,
      unlistedCount: 8,
      moveCount: 0,
      removeCount: 0,
      ledgerLink: '/fixedassetledger?inventoryYearMonth=202607&storeCode=004',
    },
    {
      companyCode: 'NX',
      companyName: 'NX社名',
      storeCode: 'N001',
      storeName: '本店',
      storeDisplayText: 'N001 本店',
      inventoryStartDateDisplay: '2026/07/05',
      totalCount: 152,
      confirmedCount: 152,
      confirmedRate: 100.0,
      confirmedRateDisplay: '100%',
      unconfirmedCount: 0,
      unusedCount: 0,
      unlistedCount: 0,
      moveCount: 0,
      removeCount: 0,
      ledgerLink: '/fixedassetledger?inventoryYearMonth=202607&storeCode=N001',
    },
    {
      companyCode: 'NX',
      companyName: 'NX社名',
      storeCode: 'N002',
      storeName: '第二店',
      storeDisplayText: 'N002 第二店',
      inventoryStartDateDisplay: '2026/07/06',
      totalCount: 100,
      confirmedCount: 78,
      confirmedRate: 78.0,
      confirmedRateDisplay: '78%',
      unconfirmedCount: 15,
      unusedCount: 5,
      unlistedCount: 2,
      moveCount: 0,
      removeCount: 0,
      ledgerLink: '/fixedassetledger?inventoryYearMonth=202607&storeCode=N002',
    },
  ],
};
